import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RateType } from '@cyberlabrs/helpers';
import { MixerLoader, Table, TableCell, TableHeadCell, TableRow } from '../../Common';
import { ProjectAssigneeCurrencyRowDto, ProjectAssigneeRateRowDto, ProjectAssigneesWithUserInfoDto } from '../../../commonTypes/project.types';
import { PublicUserDto } from '../../../commonTypes/user.types';
import useHttpPostProjectAssigneeRateHistory from '../../../hooks/project/projectApi/useHttpPostProjectAssigneeRateHistory';
import useHttpGetProjectAssigneeCurrencyHistory from '../../../hooks/project/projectApi/useHttpGetProjectAssigneeCurrencyHistory';
import useHttpGetDisplayProfilesByUserSubsIncludingDetached from '../../../hooks/user/userApi/useHttpGetDisplayProfilesByUserSubsIncludingDetached';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatToMonthDayYear } from '../../../util/date/dateFormater';
import { getCurrencySymbol } from '../../../util/general';

type ProjectAssigneeRateHistoryProps = {
    projectId: string;
    projectStartDate?: string;
    currency: string;
    assignees: ProjectAssigneesWithUserInfoDto[];
    organizationMembers: PublicUserDto[];
    enabled: boolean;
    refreshKey: number;
};

export const ProjectAssigneeRateHistory = ({
    projectId,
    projectStartDate,
    currency,
    assignees,
    organizationMembers,
    enabled,
    refreshKey,
}: ProjectAssigneeRateHistoryProps) => {
    const { t } = useTranslation();
    const { rows, isReady } = useHttpPostProjectAssigneeRateHistory(projectId, enabled, projectStartDate, refreshKey);
    const { rows: currencyRows, isReady: isCurrencyReady } = useHttpGetProjectAssigneeCurrencyHistory(
        projectId,
        enabled,
        projectStartDate,
        refreshKey
    );
    const { profilesBySub, exec: fetchDisplayProfiles } = useHttpGetDisplayProfilesByUserSubsIncludingDetached();

    const historyUserSubs = useMemo(() => Array.from(new Set(rows.map(row => row.userSub).filter(Boolean))), [rows]);

    useEffect(() => {
        if (!historyUserSubs.length) return;
        return fetchDisplayProfiles(historyUserSubs);
    }, [historyUserSubs, fetchDisplayProfiles]);

    const nameBySub = new Map<string, string>();
    const organizationMemberSubs = new Set(organizationMembers.map(member => member.userSub));

    Object.entries(profilesBySub).forEach(([userSub, profile]) => {
        const name =
            profile.displayName?.trim() ||
            `${profile.givenName || profile.profile?.givenName || ''} ${profile.familyName || profile.profile?.familyName || ''}`.trim() ||
            profile.email;
        if (name) {
            nameBySub.set(userSub, name);
        }
    });

    assignees.forEach(a => {
        if (!nameBySub.has(a.userSub)) {
            const name = `${a.givenName || ''} ${a.familyName || ''}`.trim() || a.email;
            if (name) {
                nameBySub.set(a.userSub, name);
            }
        }
    });

    organizationMembers.forEach(member => {
        if (!nameBySub.has(member.userSub)) {
            const name = `${member.givenName || ''} ${member.familyName || ''}`.trim() || member.email;
            if (name) {
                nameBySub.set(member.userSub, name);
            }
        }
    });

    const fmtRate = (rate: number | null, currencyCode?: string) => {
        if (rate == null) return t('common.notSet');
        return `${getCurrencySymbol(currencyCode || currency)}${rate.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const fmtRateType = (rateType?: RateType) => {
        if (rateType === RateType.FIXED) return 'Fixed';
        if (rateType === RateType.HOURLY) return 'Hourly';
        return t('common.notSet');
    };

    const derivedRows = useMemo(() => {
        const maxDate = (left: string, right: string) => (left > right ? left : right);
        const minDate = (left: string | null, right: string | null) => {
            if (left == null) return right;
            if (right == null) return left;
            return left < right ? left : right;
        };

        const intersects = (rateRow: ProjectAssigneeRateRowDto, currencyRow: ProjectAssigneeCurrencyRowDto) => {
            if (rateRow.userSub !== currencyRow.userSub || rateRow.projectId !== currencyRow.projectId) return false;
            const rateEnd = rateRow.effectiveTo ?? '9999-12-31';
            const currencyEnd = currencyRow.effectiveTo ?? '9999-12-31';
            return rateRow.effectiveFrom <= currencyEnd && currencyRow.effectiveFrom <= rateEnd;
        };

        return rows.flatMap(rateRow => {
            const overlappingCurrencies = currencyRows
                .filter(currencyRow => intersects(rateRow, currencyRow))
                .sort((left, right) => left.effectiveFrom.localeCompare(right.effectiveFrom));

            if (!overlappingCurrencies.length) {
                return [
                    {
                        ...rateRow,
                        displayCurrencyCode: currency,
                    },
                ];
            }

            return overlappingCurrencies.map(currencyRow => ({
                ...rateRow,
                id: `${rateRow.id}:${currencyRow.id || currencyRow.effectiveFrom}`,
                effectiveFrom: maxDate(rateRow.effectiveFrom, currencyRow.effectiveFrom),
                effectiveTo: minDate(rateRow.effectiveTo, currencyRow.effectiveTo),
                displayCurrencyCode: currencyRow.currencyCode,
            }));
        });
    }, [rows, currencyRows, currency]);

    if (!isReady || !isCurrencyReady) {
        return <MixerLoader position="centerOnParent" />;
    }

    if (!derivedRows.length) {
        return <p className="paragraph-3 project-overview-tab__empty">{t('projects.assigneeRateHistoryEmpty')}</p>;
    }

    return (
        <div className="project-assignee-rate-history">
            <Table className="project-assignee-rate-history__table">
                <TableRow>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('projects.assigneeRateHistoryAssignee')}</TableHeadCell>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('common.rate')}</TableHeadCell>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('common.rateType')}</TableHeadCell>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('projects.assigneeRateHistoryStart')}</TableHeadCell>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('projects.assigneeRateHistoryEnd')}</TableHeadCell>
                </TableRow>
                {derivedRows.map(row => {
                    const displayName = nameBySub.get(row.userSub) || t('projects.assigneeRateHistoryUnknownAssignee');
                    const isProfileLinkEnabled = organizationMemberSubs.has(row.userSub);
                    return (
                        <TableRow key={row.id}>
                            <TableCell>
                                {isProfileLinkEnabled ? (
                                    <Link className="paragraph-3 project-assignee-rate-history__assignee-link" to={`/profile/${row.userSub}`}>
                                        {displayName}
                                    </Link>
                                ) : (
                                    <span className="paragraph-3 project-assignee-rate-history__assignee-link project-assignee-rate-history__assignee-link--detached">
                                        {displayName}
                                    </span>
                                )}
                            </TableCell>
                            <TableCell>{fmtRate(row.rate, row.displayCurrencyCode)}</TableCell>
                            <TableCell>{fmtRateType(row.rateType)}</TableCell>
                            <TableCell>{formatToMonthDayYear(row.effectiveFrom)}</TableCell>
                            <TableCell>
                                {row.effectiveTo == null ? (
                                    <span className="paragraph-3 project-assignee-rate-history__active">
                                        {t('projects.assigneeRatePeriodActive')}
                                    </span>
                                ) : (
                                    formatToMonthDayYear(row.effectiveTo)
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </Table>
        </div>
    );
};
